import { useState, useMemo, useCallback } from 'react';
import EditableField from '../components/EditableField';
import deals from "../data/deals";
import DealCard from "../components/DealCard";
import useDealStore from "../store/dealStore";
import useAuthStore from "../store/authStore";
import useDialogStore from "../store/dialogStore";
import useMarketData from "../hooks/useMarketData";
import useLiveDeals from "../hooks/useLiveDeals";
import { API_STATUS } from "../services/marketDataService";
import LoopNetStatusBanner from "../components/LoopNetStatusBanner";
import { MODULES } from "../data/moduleTransitions.js";
import "../styles/deal-lead.css";
import "../styles/actionCenter.css";

const DEFAULT_FILTERS = {
  search: '',
  source: 'all',
  minPrice: '',
  maxPrice: '',
  minUnits: '',
  maxUnits: '',
  minScore: 0,
  buildingType: 'all',
  sortBy: 'score_desc',
};

const DEFAULT_INVESTOR_PROFILE = {
  riskTolerance: 'moderate',
  preferredMarket: 'all',
  dealSize: 'medium',
  minCapRate: 5.0,
};

const AGE_CLASS_OPTIONS = [
  { grade: 'A', range: '0-10 yrs' },
  { grade: 'B', range: '10-25 yrs' },
  { grade: 'C', range: '25-40 yrs' },
  { grade: 'D', range: '40+ yrs' },
];

// ─── Quality Thresholds ────────────────────────────────────────────────────
const MIN_UNITS = 10;   // hard floor — no deals with fewer than 10 units (applies to ALL sources)
const MAX_UNITS = 1000; // soft ceiling — 100+ is fine; only exclude absurdly large/erroneous entries
const MIN_CAP_RATE = 4.0;  // soft floor — exclude deals with a cap rate below this (if provided)

// Unified quality filter applied to every deal regardless of source
function passesQualityGate(deal) {
  const units = Number(deal.units) || 0;
  // Live LoopNet deals without real unit data carry a display fallback
  // (units: 1 or sqft-derived) plus unitsKnown: false. The unit-count gates
  // must only apply when the unit count is actually known, otherwise every
  // sparse live listing is silently filtered out.
  const unitsAreKnown = deal.source === 'loopnet_live' ? deal.unitsKnown === true : true;
  // Hard floor: must have at least 10 units
  if (unitsAreKnown && units > 0 && units < MIN_UNITS) return false;
  // Soft ceiling: sanity-check — exclude clearly erroneous unit counts
  if (unitsAreKnown && units > MAX_UNITS) return false;
  // Soft cap-rate floor: only exclude if cap rate is explicitly provided AND below threshold
  const capRate = Number(deal.capRate);
  if (Number.isFinite(capRate) && capRate > 0 && capRate < MIN_CAP_RATE) return false;
  return true;
}

// ─── Duplicate Detection ───────────────────────────────────────────────────
function normalizeAddressKey(deal) {
  const address = [deal.location, deal.city, deal.state, deal.zip]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/\b(street|st)\b/g, 'st')
    .replace(/\b(avenue|ave)\b/g, 'ave')
    .replace(/\b(boulevard|blvd)\b/g, 'blvd')
    .replace(/\b(road|rd)\b/g, 'rd')
    .replace(/\b(drive|dr)\b/g, 'dr')
    .replace(/\b(north|n)\b/g, 'n')
    .replace(/\b(south|s)\b/g, 's')
    .replace(/\b(east|e)\b/g, 'e')
    .replace(/\b(west|w)\b/g, 'w')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  return address.length >= 12 ? address : '';
}

function getRelativeDifference(a, b) {
  const first = Number(a);
  const second = Number(b);
  if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) {
    return Infinity;
  }
  return Math.abs(first - second) / Math.max(first, second);
}

function areNearDuplicateDeals(a, b) {
  const sameZip = a.zip && b.zip && String(a.zip) === String(b.zip);
  const sameCityState = a.city && b.city && a.state && b.state
    && a.city.toLowerCase() === b.city.toLowerCase()
    && a.state.toLowerCase() === b.state.toLowerCase();
  const closePrice = getRelativeDifference(a.price, b.price) <= 0.12;
  const closeUnits = getRelativeDifference(a.units, b.units) <= 0.12;
  const sameAssetType = !a.buildingType || !b.buildingType
    || a.buildingType.toLowerCase() === b.buildingType.toLowerCase();

  return sameAssetType && closePrice && closeUnits && (sameZip || sameCityState);
}

function detectDuplicates(dealList) {
  const groups = {};
  dealList.forEach((a, i) => {
    dealList.slice(i + 1).forEach(b => {
      const sameListingId = a.externalId && b.externalId && String(a.externalId) === String(b.externalId);
      const addressA = normalizeAddressKey(a);
      const addressB = normalizeAddressKey(b);
      const sameAddress = addressA && addressB && addressA === addressB;
      const latA = Number(a.rawListing?.lat ?? a.lat);
      const lngA = Number(a.rawListing?.lng ?? a.lng);
      const latB = Number(b.rawListing?.lat ?? b.lat);
      const lngB = Number(b.rawListing?.lng ?? b.lng);
      const sameCoords = [latA, lngA, latB, lngB].every(Number.isFinite)
        && Math.abs(latA - latB) < 0.0002
        && Math.abs(lngA - lngB) < 0.0002;
      const nearDuplicate = areNearDuplicateDeals(a, b);

      if (sameListingId || sameAddress || sameCoords || nearDuplicate) {
        const key = [a.id, b.id].sort().join('-');
        if (!groups[key]) groups[key] = new Set();
        groups[key].add(a.id);
        groups[key].add(b.id);
      }
    });
  });
  const merged = [];
  Object.values(groups).forEach(set => {
    const existing = merged.find(g => [...set].some(id => g.has(id)));
    if (existing) [...set].forEach(id => existing.add(id));
    else merged.push(set);
  });
  const result = {};
  merged.forEach((group, idx) => {
    group.forEach(id => { result[id] = idx + 1; });
  });
  return result;
}

// ─── Investor Fit Score ────────────────────────────────────────────────────
function getDealRank(deal) {
  const sourceRank = {
    loopnet_live: 4,
    broker_email: 3,
    listings: 2,
    manual_upload: 1,
  };
  return [
    Number(deal.score) || 0,
    sourceRank[deal.source] || 0,
    Number(deal.capRate) || 0,
  ];
}

function compareDealRank(a, b) {
  const rankA = getDealRank(a);
  const rankB = getDealRank(b);
  for (let i = 0; i < rankA.length; i += 1) {
    if (rankA[i] !== rankB[i]) return rankB[i] - rankA[i];
  }
  return 0;
}

function computeFitScore(deal, profile) {
  let score = 0;
  if (profile.preferredMarket === 'all' || deal.state === profile.preferredMarket) {
    score += 30;
  } else {
    score += 5;
  }
  const capRate = deal.capRate ?? 0;
  if (profile.riskTolerance === 'aggressive') {
    score += capRate >= 6.5 ? 35 : capRate >= 6.0 ? 28 : capRate >= 5.5 ? 18 : 8;
  } else if (profile.riskTolerance === 'moderate') {
    score += capRate >= 5.5 && capRate <= 7.0 ? 35 : capRate >= 5.0 ? 22 : 10;
  } else {
    score += capRate >= 4.5 && capRate <= 6.0 ? 35 : capRate >= 4.0 ? 25 : 12;
  }
  const units = deal.units ?? 0;
  if (profile.dealSize === 'small' && units < 40) score += 20;
  else if (profile.dealSize === 'medium' && units >= 40 && units <= 65) score += 20;
  else if (profile.dealSize === 'large' && units > 65) score += 20;
  else score += 8;
  score += capRate >= profile.minCapRate ? 15 : capRate >= profile.minCapRate - 0.5 ? 8 : 0;
  return Math.min(100, Math.round(score));
}

function getFitLabel(score) {
  if (score >= 85) return { label: 'Excellent Fit', color: '#1d4ed8' };
  if (score >= 70) return { label: 'Good Fit', color: '#3b82f6' };
  if (score >= 50) return { label: 'Fair Fit', color: '#1d4ed8' };
  return { label: 'Poor Fit', color: '#0f2d5e' };
}

function getMomentumSignal(deal, duplicateGroup) {
  const score = Number(deal.score) || 0;
  const capRate = Number(deal.capRate) || 0;
  const units = Number(deal.units) || 0;
  const velocity = Math.round(Math.min(98, Math.max(42, score * 0.62 + capRate * 4 + units * 0.16 + (duplicateGroup ? 9 : 0))));
  const label = velocity >= 82 ? 'Heating Up' : velocity >= 66 ? 'Active Watch' : 'Likely Patient';
  const note = velocity >= 82
    ? 'Expect faster broker follow-up and possible competing offers.'
    : velocity >= 66
      ? 'Good candidate for outreach before broader attention builds.'
      : 'Could support a slower, more price-disciplined approach.';
  return { velocity, label, note };
}

function getRealitySignal(deal) {
  const capRate = Number(deal.capRate) || 0;
  const score = Number(deal.score) || 0;
  const pricePerUnit = deal.units ? deal.price / deal.units : 0;
  const checks = [
    { label: 'Rent upside credible', pass: score >= 72 },
    { label: 'Pricing not stretched', pass: pricePerUnit < 240000 },
    { label: 'Yield clears screen', pass: capRate >= 5.25 },
  ];
  const passed = checks.filter(check => check.pass).length;
  const rating = Math.round((passed / checks.length) * 100);
  return {
    checks,
    rating,
    label: rating >= 80 ? 'Clean Story' : rating >= 55 ? 'Verify Claims' : 'High Skepticism',
  };
}

function getApiMarketSignal(deal, marketStats) {
  const avgCapRate = Number(marketStats?.avgCapRate);
  const totalListings = Number(marketStats?.totalListings);
  // Adjust deal score based on market cap rate vs deal cap rate
  const capRateBoost = Number.isFinite(avgCapRate) && avgCapRate > 0
    ? Math.max(-8, Math.min(8, (Number(deal.capRate || 0) - avgCapRate) * 2))
    : 0;
  const marketDepthBoost = Number.isFinite(totalListings) && totalListings > 50 ? 2 : 0;
  const liveScore = Math.round(Math.max(0, Math.min(100, Number(deal.score || 0) + capRateBoost + marketDepthBoost)));
  return {
    liveScore,
    avgCapRate: Number.isFinite(avgCapRate) ? avgCapRate : null,
    totalListings: Number.isFinite(totalListings) ? totalListings : null,
    isLive: Boolean(marketStats?.isLive || API_STATUS.hasLoopNetKey),
  };
}

function filteredPreferredState(dealList) {
  const counts = dealList.reduce((acc, deal) => {
    acc[deal.state] = (acc[deal.state] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'TX';
}

function getBuildingAgeGrade(yearBuilt) {
  const builtYear = Number(yearBuilt);
  if (!Number.isFinite(builtYear) || builtYear <= 0) return null;

  const age = Math.max(0, new Date().getFullYear() - builtYear);
  if (age <= 10) return 'A';
  if (age <= 25) return 'B';
  if (age <= 40) return 'C';
  return 'D';
}

function getBuildingAgeClassDetails(yearBuilt) {
  const grade = getBuildingAgeGrade(yearBuilt);
  const builtYear = Number(yearBuilt);
  const age = Number.isFinite(builtYear) && builtYear > 0
    ? Math.max(0, new Date().getFullYear() - builtYear)
    : null;

  const labels = {
    A: '0-10 yrs',
    B: '10-25 yrs',
    C: '25-40 yrs',
    D: '40+ yrs',
  };

  return {
    grade,
    age,
    label: grade ? labels[grade] : 'Select a deal',
  };
}

// ──────────────────────────────────────────────────────────────────────────

function DealLeadPage() {
  const { initializeDeal, dealState, forceNavigateToModule, resetDeal } = useDealStore();
  const confirm = useDialogStore(state => state.confirm);
  const { user } = useAuthStore();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailDeal, setDetailDeal] = useState(null);
  // editableDeal = local copy of detailDeal that the user can modify in the modal
  const [editableDeal, setEditableDeal] = useState(null);

  const openDetailModal = useCallback((deal) => {
    setDetailDeal(deal);
    setEditableDeal({ ...deal }); // fresh editable copy
  }, []);

  const patchEditableDeal = useCallback((field, value) => {
    setEditableDeal(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  // Part 3 — Post-confirmation popup state
  const [showProceedPopup, setShowProceedPopup] = useState(false);
  // Part 5 — Missing documents gate state
  const [showMissingDocsGate, setShowMissingDocsGate] = useState(false);

  const [investorProfile, setInvestorProfile] = useState(DEFAULT_INVESTOR_PROFILE);
  const [profileOpen, setProfileOpen] = useState(true);

  const liveStateCode = investorProfile.preferredMarket === 'all'
    ? filteredPreferredState(deals)
    : investorProfile.preferredMarket;

  const { stateStats: liveMarketStats, loading: liveLoading, error: liveError, lastFetchedAt, refresh } =
    useMarketData(liveStateCode);

  const {
    deals: loopnetDeals,
    loading: liveDealsLoading,
    error: liveDealsError,
    apiStatus: liveDealsApiStatus,
    lastFetchedAt: liveDealsFetchedAt,
    refresh: refreshLiveDeals,
  } = useLiveDeals({ stateCode: liveStateCode, propertyType: 'multifamily', limit: 30 });

  // Filter LoopNet deals — the hook already applies a unit floor, but we re-apply the full
  // quality gate here as well (cap rate, unit ceiling) for belt-and-suspenders safety.
  const qualifiedLoopnetDeals = useMemo(
    () => loopnetDeals.filter(passesQualityGate),
    [loopnetDeals]
  );

  // Merge sources: all deals go through the same unified quality gate.
  // Curated deals from deals.js are included, but any that fall below thresholds are removed.
  const rawActiveDeals = useMemo(
    () => [...qualifiedLoopnetDeals, ...deals.filter(passesQualityGate)],
    [qualifiedLoopnetDeals]
  );

  const usingLiveDeals = qualifiedLoopnetDeals.length > 0;

  const setFilter = (key, value) =>
    setFilters(prev => ({ ...prev, [key]: value }));
  const setProfile = (key, value) =>
    setInvestorProfile(prev => ({ ...prev, [key]: value }));

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  // ── Duplicate detection & deduplication (Fix 8) ──
  const duplicateMap = useMemo(() => detectDuplicates(rawActiveDeals), [rawActiveDeals]);

  // Remove duplicates from display — keep only the first occurrence of each group
  const activeDeals = useMemo(() => {
    const groupWinners = rawActiveDeals.reduce((acc, deal) => {
      const groupId = duplicateMap[deal.id];
      if (groupId === undefined) return acc;
      const current = acc[groupId];
      if (!current || compareDealRank(deal, current) < 0) {
        acc[groupId] = deal;
      }
      return acc;
    }, {});

    return rawActiveDeals.filter(deal => {
      const groupId = duplicateMap[deal.id];
      if (groupId === undefined) return true;
      return groupWinners[groupId]?.id === deal.id;
    });
  }, [rawActiveDeals, duplicateMap]);

  const removedDuplicatesCount = rawActiveDeals.length - activeDeals.length;
  const duplicateGroupCount = useMemo(
    () => new Set(Object.values(duplicateMap)).size,
    [duplicateMap]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.source !== 'all') count++;
    if (filters.minPrice) count++;
    if (filters.maxPrice) count++;
    if (filters.minUnits) count++;
    if (filters.maxUnits) count++;
    if (filters.minScore > 0) count++;
    if (filters.buildingType !== 'all') count++;
    return count;
  }, [filters]);

  const dealsWithFit = useMemo(() =>
    activeDeals.map(d => ({ ...d, fitScore: computeFitScore(d, investorProfile) })),
    [activeDeals, investorProfile]
  );

  const classifiedDeal = useMemo(() => {
    if (selectedDeal) return selectedDeal;
    if (!dealState.dealName) return null;
    return activeDeals.find(deal => deal.name === dealState.dealName) || null;
  }, [activeDeals, dealState.dealName, selectedDeal]);

  const selectedAgeClass = useMemo(
    () => getBuildingAgeClassDetails(classifiedDeal?.yearBuilt),
    [classifiedDeal]
  );

  const filteredDeals = useMemo(() => {
    let result = [...dealsWithFit];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(d =>
        d.name.toLowerCase().includes(q) ||
        d.city?.toLowerCase().includes(q) ||
        d.state?.toLowerCase().includes(q) ||
        d.location?.toLowerCase().includes(q)
      );
    }
    if (filters.source !== 'all') {
      result = result.filter(d => d.source === filters.source);
    }
    if (filters.minPrice) result = result.filter(d => d.price >= Number(filters.minPrice));
    if (filters.maxPrice) result = result.filter(d => d.price <= Number(filters.maxPrice));
    if (filters.minUnits) result = result.filter(d => d.units >= Number(filters.minUnits));
    if (filters.maxUnits) result = result.filter(d => d.units <= Number(filters.maxUnits));
    if (filters.minScore > 0) result = result.filter(d => d.score >= filters.minScore);
    if (filters.buildingType !== 'all') {
      result = result.filter(d =>
        d.buildingType?.toLowerCase() === filters.buildingType.toLowerCase()
      );
    }
    switch (filters.sortBy) {
      case 'score_desc': result.sort((a, b) => b.score - a.score); break;
      case 'score_asc': result.sort((a, b) => a.score - b.score); break;
      case 'price_asc': result.sort((a, b) => a.price - b.price); break;
      case 'price_desc': result.sort((a, b) => b.price - a.price); break;
      case 'units_desc': result.sort((a, b) => b.units - a.units); break;
      case 'units_asc': result.sort((a, b) => a.units - b.units); break;
      case 'fit_desc': result.sort((a, b) => b.fitScore - a.fitScore); break;
      default: break;
    }
    return result;
  }, [filters, dealsWithFit]);

  const sourceHealth = useMemo(() => {
    const hotDeals = dealsWithFit.filter((deal) => getMomentumSignal(deal, duplicateMap[deal.id]).velocity >= 82).length;
    const cleanDeals = dealsWithFit.filter((deal) => getRealitySignal(deal).rating >= 80).length;
    return [
      { label: 'Live sale listings', value: qualifiedLoopnetDeals.length, sub: API_STATUS.hasLoopNetKey ? `LoopNet active · ${liveStateCode}` : 'connect LoopNet key' },
      { label: 'Curated portfolio deals', value: deals.length, sub: 'always shown' },
      { label: 'Hot momentum', value: hotDeals, sub: 'likely competitive' },
      { label: 'Clean reality checks', value: cleanDeals, sub: 'claims look supportable' },
    ];
  }, [dealsWithFit, duplicateMap, liveStateCode, loopnetDeals.length, usingLiveDeals]);

  const buildDealPayload = (deal) => ({
    name: deal.name,
    source: deal.source,
    price: deal.price,
    location: { address: deal.location, city: deal.city, state: deal.state, zip: deal.zip },
    property: { units: deal.units, buildingType: deal.buildingType, yearBuilt: deal.yearBuilt, squareFeet: deal.squareFeet },
    initialAIScore: deal.score,
    apiContext: {
      module1ListingsSource: deal.source === 'loopnet_live' ? 'LoopNet /loopnet/sale/searchByCity + /loopnet/property/SaleDetails' : 'local deals.js',
      loopnetListingId: deal.externalId || null,
      loopnetListingUrl: deal.sourceUrl || null,
      loopnetConnected: API_STATUS.hasLoopNetKey,
      liveStateCode,
      liveMarketStats,
    },
  });

  const handleSelectDeal = (deal) => setSelectedDeal(deal);

  // Part 3: Confirm deal but do NOT auto-navigate — just save state and show View Details
  const handleConfirmDeal = () => {
    if (!selectedDeal) return;
    initializeDeal(buildDealPayload(selectedDeal));
    // Do NOT navigate — user must click "View Details" to proceed
  };

  // Part 3: View Details clicked → check for OM / docs, then show popup
  const handleViewDetails = () => {
    if (!dealState.dealId) return;
    // Part 5: Check if the deal is a LoopNet deal without an OM
    const isLoopNetDeal = dealState.rawDealData?.source === 'loopnet_live' ||
      dealState.rawDealData?.apiContext?.loopnetConnected;
    const hasOM = dealState.financialDocuments?.omUploaded;

    if (isLoopNetDeal && !hasOM) {
      setShowMissingDocsGate(true);
    } else {
      setShowProceedPopup(true);
    }
  };

  // Part 3: Go to Underwriting Engine from popup
  const handleGoToUnderwriting = () => {
    setShowProceedPopup(false);
    forceNavigateToModule(4);
  };

  const sources = [
    { id: 'all', name: 'All Sources', icon: '📋' },
    { id: 'loopnet_live', name: 'LoopNet Live', icon: '🟢' },
    { id: 'broker_email', name: 'Broker Email', icon: '📧' },
    { id: 'listings', name: 'Listings', icon: '🏢' },
    { id: 'manual_upload', name: 'Manual Upload', icon: '📤' },
  ];

  return (
    <div className="deal-lead-container">

      {/* Page header */}
      <div className="deal-lead-header">
        <div className="header-content">
          <h1>Deal Lead Generation</h1>
          <p className="module-description">{MODULES[1].output}</p>
        </div>
        <div className="user-context">
          <span className="user-info">
            {user?.name}
          </span>
        </div>
      </div>

      {/* ── LoopNet API Status Banner ── */}
      <LoopNetStatusBanner
        loading={liveDealsLoading}
        error={liveDealsError}
        apiStatus={liveDealsApiStatus}
        usingLiveDeals={usingLiveDeals}
        liveCount={qualifiedLoopnetDeals.length}
        location={liveStateCode}
        onRetry={refreshLiveDeals}
      />

      {/* ── Decision Gate Banner ── */}
      {!dealState.dealId && (
        <div className="decision-gate-banner">
          <div className="dgb-icon">🔒</div>
          <div className="dgb-body">
            <strong>Deal selection required</strong>
            <p>Browse the deals below, select one, and click <em>Confirm Deal Selection</em>, then <em>View Details</em> to proceed to Underwriting.</p>
          </div>
        </div>
      )}
      {dealState.dealId && (
        <div className="decision-gate-banner confirmed">
          <div className="dgb-icon"></div>
          <div className="dgb-body">
            <strong>Deal confirmed — click View Details to proceed to Underwriting</strong>
            <div className="selection-confirm-card">
              <span className="scc-check">✓</span>
              <span className="scc-name">{dealState.dealName}</span>
              <button className="scc-change" onClick={() => {
                confirm({
                  title: 'Reset Active Deal',
                  message: 'Reset the active deal and start over from deal selection?',
                  type: 'warning',
                  confirmText: 'Reset Deal',
                  onConfirm: () => {
                    resetDeal();
                    setSelectedDeal(null);
                    setShowProceedPopup(false);
                    setShowMissingDocsGate(false);
                  }
                });
              }}>Change deal</button>
            </div>
          </div>
          <button className="dgb-confirm-btn dgb-view-details-btn" onClick={handleViewDetails}>
            View Details →
          </button>
        </div>
      )}

      {/* ── Duplicate Removal Banner ── */}
      {removedDuplicatesCount > 0 && (
        <div className="duplicate-alert-banner">
          <span className="duplicate-alert-icon"></span>
          <div className="duplicate-alert-text">
            <strong>Duplicates Removed:</strong> {removedDuplicatesCount} duplicate listing{removedDuplicatesCount > 1 ? 's' : ''} automatically hidden.
            Only the best-ranked occurrence of each property is shown.
          </div>
        </div>
      )}

      {/* ── Intel Strip ── */}
      <div className="deal-intel-strip">
        {sourceHealth.map(item => (
          <div className="deal-intel-card" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <em>{item.sub}</em>
          </div>
        ))}
      </div>

      <div className="deal-lead-content">

        {/* ── Filter panel ── */}
        <div className="filter-section">

          <div className="filter-header">
            <h3>
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-badge">{activeFilterCount}</span>
              )}
            </h3>
            <div className="filter-header-actions">
              {activeFilterCount > 0 && (
                <button className="reset-filters-btn" onClick={resetFilters}>Reset</button>
              )}
              <button className="toggle-filters-btn" onClick={() => setFiltersOpen(o => !o)}>
                {filtersOpen ? '▲' : '▼'}
              </button>
            </div>
          </div>

          {filtersOpen && (
            <>
              <div className="filter-group">
                <label className="filter-label">Search</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Name, city, state…"
                  value={filters.search}
                  onChange={e => setFilter('search', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label className="filter-label">Source</label>
                <div className="source-filters">
                  {sources.map(s => (
                    <button
                      key={s.id}
                      className={`source-filter ${filters.source === s.id ? 'active' : ''}`}
                      onClick={() => setFilter('source', s.id)}
                    >
                      <span className="source-icon">{s.icon}</span>
                      <span className="source-name">{s.name}</span>
                      <span className="source-count">
                        {s.id === 'all'
                          ? activeDeals.length
                          : activeDeals.filter(d => d.source === s.id).length}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Price Range ($)</label>
                <div className="filter-range-row">
                  <input type="number" className="filter-input" placeholder="Min"
                    value={filters.minPrice} onChange={e => setFilter('minPrice', e.target.value)} />
                  <span className="filter-range-sep">–</span>
                  <input type="number" className="filter-input" placeholder="Max"
                    value={filters.maxPrice} onChange={e => setFilter('maxPrice', e.target.value)} />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Units</label>
                <div className="filter-range-row">
                  <input type="number" className="filter-input" placeholder="Min"
                    value={filters.minUnits} onChange={e => setFilter('minUnits', e.target.value)} />
                  <span className="filter-range-sep">–</span>
                  <input type="number" className="filter-input" placeholder="Max"
                    value={filters.maxUnits} onChange={e => setFilter('maxUnits', e.target.value)} />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">
                  Min AI Score: <strong>{filters.minScore}</strong>
                </label>
                <input type="range" min="0" max="100" value={filters.minScore}
                  onChange={e => setFilter('minScore', Number(e.target.value))}
                  className="filter-slider" />
                <div className="filter-slider-labels">
                  <span>0</span><span>50</span><span>100</span>
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Building Type</label>
                <select className="filter-input" value={filters.buildingType}
                  onChange={e => setFilter('buildingType', e.target.value)}>
                  <option value="all">All Types</option>
                  <option value="Multifamily">Multifamily</option>
                  <option value="Mixed-Use">Mixed-Use</option>
                  <option value="SFR Portfolio">SFR Portfolio</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Sort By</label>
                <select className="filter-input" value={filters.sortBy}
                  onChange={e => setFilter('sortBy', e.target.value)}>
                  <option value="score_desc">AI Score: High → Low</option>
                  <option value="score_asc">AI Score: Low → High</option>
                  <option value="fit_desc">Investor Fit: Best First</option>
                  <option value="price_asc">Price: Low → High</option>
                  <option value="price_desc">Price: High → Low</option>
                  <option value="units_desc">Units: Most first</option>
                  <option value="units_asc">Units: Least first</option>
                </select>
              </div>
            </>
          )}

          {/* ── Investor Fit Score — Profile Panel ── */}
          <div className="fit-profile-section">
            <div className="fit-profile-header" onClick={() => setProfileOpen(o => !o)}>
              <span className="fit-profile-title">🎯 My Investor Profile</span>
              <span className="toggle-filters-btn">{profileOpen ? '▲' : '▼'}</span>
            </div>
            <p className="fit-profile-subtitle">Personalize fit scores to your strategy</p>

            {profileOpen && (
              <>
                <div className="filter-group">
                  <label className="filter-label">Risk Tolerance</label>
                  <div className="fit-pill-group">
                    {['conservative', 'moderate', 'aggressive'].map(r => (
                      <button
                        key={r}
                        className={`fit-pill ${investorProfile.riskTolerance === r ? 'active' : ''}`}
                        onClick={() => setProfile('riskTolerance', r)}
                      >
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Preferred Market</label>
                  <select className="filter-input" value={investorProfile.preferredMarket}
                    onChange={e => setProfile('preferredMarket', e.target.value)}>
                    <option value="all">Any Market</option>
                    <option value="TX">Texas (TX)</option>
                    <option value="FL">Florida (FL)</option>
                    <option value="AZ">Arizona (AZ)</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Deal Size</label>
                  <div className="fit-pill-group">
                    {[
                      { val: 'small', label: 'Small\n<40u' },
                      { val: 'medium', label: 'Medium\n40-65u' },
                      { val: 'large', label: 'Large\n>65u' },
                    ].map(({ val, label }) => (
                      <button
                        key={val}
                        className={`fit-pill ${investorProfile.dealSize === val ? 'active' : ''}`}
                        onClick={() => setProfile('dealSize', val)}
                      >
                        {label.split('\n').map((l, i) => (
                          <span key={i} style={{ display: 'block', lineHeight: i === 0 ? '1.2' : '1', fontSize: i === 1 ? '9px' : '11px' }}>{l}</span>
                        ))}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <label className="filter-label">
                    Min Cap Rate: <strong>{investorProfile.minCapRate.toFixed(1)}%</strong>
                  </label>
                  <input type="range" min="3" max="9" step="0.5"
                    value={investorProfile.minCapRate}
                    onChange={e => setProfile('minCapRate', Number(e.target.value))}
                    className="filter-slider" />
                  <div className="filter-slider-labels">
                    <span>3%</span><span>6%</span><span>9%</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Deals grid ── */}
        <div className="deals-section">
          <div className="deals-header">
            <h3>Available Deals ({filteredDeals.length})</h3>
            {selectedDeal && (!dealState.dealId || dealState.dealName !== selectedDeal.name) && (
              <div className="pending-deal-indicator">
                <span>Selected: {selectedDeal.name}</span>
                <span className="selected-age-class-chip">
                  Class {selectedAgeClass.grade || 'Unknown'}
                  {selectedAgeClass.age != null ? ` - ${selectedAgeClass.age} yrs` : ''}
                </span>
                <button onClick={handleConfirmDeal}>Confirm Deal Selection</button>
              </div>
            )}
            {dealState.dealId && (
              <div className="active-deal-indicator">
                <span>✓ Deal Active: {dealState.dealName}</span>
                <span className="selected-age-class-chip">
                  Class {selectedAgeClass.grade || 'Unknown'}
                  {selectedAgeClass.age != null ? ` - ${selectedAgeClass.age} yrs` : ''}
                </span>
                <button className="continue-button view-details-btn" onClick={handleViewDetails}>
                  View Details →
                </button>
              </div>
            )}
          </div>

          {classifiedDeal && (
            <div className="selected-age-class-panel">
              <div className="selected-age-class-copy">
                <span>Building Age Class</span>
                <strong>
                  {classifiedDeal.name} comes under Class {selectedAgeClass.grade || 'Unknown'}
                </strong>
                <p>
                  {selectedAgeClass.grade
                    ? `Built in ${classifiedDeal.yearBuilt}, approximately ${selectedAgeClass.age} years old.`
                    : 'Year built is not available for this selected deal.'}
                </p>
              </div>
              <div className="selected-age-class-grid">
                {AGE_CLASS_OPTIONS.map(option => (
                  <div
                    key={option.grade}
                    className={`selected-age-class-tile ${selectedAgeClass.grade === option.grade ? 'active' : ''}`}
                  >
                    <strong>Class {option.grade}</strong>
                    <span>{option.range}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const filteredLive = filteredDeals.filter(d => d.source === 'loopnet_live');
            const filteredCurated = filteredDeals.filter(d => d.source !== 'loopnet_live');

            const renderDealCardItem = (deal) => {
              const dupGroup = duplicateMap[deal.id];
              const momentum = getMomentumSignal(deal, dupGroup);
              const reality = getRealitySignal(deal);
              const apiMarket = getApiMarketSignal(deal, liveMarketStats);
              return (
                <div key={deal.id} className="deal-card-wrapper">
                  <DealCard
                    deal={deal}
                    onSelect={handleSelectDeal}
                    onConfirm={handleConfirmDeal}
                    onViewDetails={(d) => openDetailModal(d)}
                    isSelected={selectedDeal?.name === deal.name}
                    isActive={dealState.dealName === deal.name}
                  />
                  <div className="deal-signal-dock">
                    <div className="deal-signal">
                      <span>Momentum</span>
                      <strong>{momentum.label}</strong>
                      <div className="deal-signal-bar"><i style={{ width: `${momentum.velocity}%` }} /></div>
                      <em>{momentum.note}</em>
                    </div>
                    <div className="deal-signal">
                      <span>Reality Check</span>
                      <strong>{reality.label}</strong>
                      <div className="deal-mini-checks">
                        {reality.checks.map(check => (
                          <b key={check.label} className={check.pass ? 'pass' : 'watch'}>{check.pass ? 'OK' : 'CHK'} {check.label}</b>
                        ))}
                      </div>
                    </div>
                    <div className="deal-signal api-live-signal">
                      <span>{apiMarket.isLive ? 'Live Market Overlay' : 'Static Overlay'}</span>
                      <strong>{apiMarket.liveScore}/100</strong>
                      <em>
                        {apiMarket.avgCapRate != null
                          ? `Market avg cap rate ${apiMarket.avgCapRate.toFixed(2)}%. `
                          : 'Uses local assumptions until LoopNet data loads.'}
                        {apiMarket.totalListings != null
                          ? `${apiMarket.totalListings.toLocaleString()} active listings.`
                          : ''}
                      </em>
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <>
                {/* ── Live Market Listings (LoopNet) ── */}
                {filteredLive.length > 0 && (
                  <>
                    <div className="deal-section-header">
                      <span className="deal-section-label-live">🟢 Live Market Listings</span>
                      <span className="deal-section-count">{filteredLive.length} deals from LoopNet</span>
                    </div>
                    <div className="deals-grid">
                      {filteredLive.map(deal => renderDealCardItem(deal))}
                    </div>
                  </>
                )}

                {/* ── Curated Portfolio Deals (always visible) ── */}
                <div className="deal-section-header" style={{ marginTop: filteredLive.length > 0 ? 24 : 0 }}>
                  <span className="deal-section-label-curated">⭐ Curated Portfolio Deals</span>
                  <span className="deal-section-count">{filteredCurated.length} hand-picked deals</span>
                </div>
                {filteredCurated.length > 0 ? (
                  <div className="deals-grid">
                    {filteredCurated.map(deal => renderDealCardItem(deal))}
                  </div>
                ) : (
                  <div className="no-deals" style={{ marginTop: 12 }}>
                    <span className="no-deals-icon">🔍</span>
                    <h3>No curated deals match your filters</h3>
                    <p>Try adjusting or resetting your filters.</p>
                    <button className="reset-filters-btn" onClick={resetFilters}>Reset Filters</button>
                  </div>
                )}

                {filteredLive.length === 0 && filteredCurated.length === 0 && (
                  <div className="no-deals">
                    <span className="no-deals-icon">📭</span>
                    <h3>No deals match your filters</h3>
                    <p>Try adjusting or resetting your filters.</p>
                    <button className="reset-filters-btn" onClick={resetFilters}>Reset Filters</button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* ── Deal Detail Modal ── */}
      {detailDeal && editableDeal && (
        <div className="ddm-overlay" onClick={() => setDetailDeal(null)}>
          <div className="ddm-panel" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="ddm-header">
              <div className="ddm-header-left">
                <div className="ddm-score-badge" style={{
                  background: editableDeal.score >= 80 ? '#dbeafe' : '#dbeafe',
                  color: editableDeal.score >= 80 ? '#1d4ed8' : editableDeal.score >= 60 ? '#1e40af' : '#1e3a8a',
                }}>
                  <EditableField
                    value={editableDeal.score}
                    type="number"
                    onSave={v => patchEditableDeal('score', Math.min(100, Math.max(0, v)))}
                    displayFormat={v => `${v}/100`}
                    style={{ color: 'inherit', fontWeight: 700 }}
                  />
                  &nbsp;
                  <span style={{ fontWeight: 400, fontSize: '12px' }}>
                    {editableDeal.score >= 80 ? 'Strong Buy' : editableDeal.score >= 60 ? 'Review' : 'Pass'}
                  </span>
                </div>
                <div>
                  <h2 className="ddm-name">
                    <EditableField
                      value={editableDeal.name}
                      onSave={v => patchEditableDeal('name', v)}
                      placeholder="Property name"
                    />
                  </h2>
                  <p className="ddm-location">
                    📍{' '}
                    <EditableField value={editableDeal.location} onSave={v => patchEditableDeal('location', v)} placeholder="Address" />{', '}
                    <EditableField value={editableDeal.city} onSave={v => patchEditableDeal('city', v)} placeholder="City" />{', '}
                    <EditableField value={editableDeal.state} onSave={v => patchEditableDeal('state', v)} placeholder="ST" />{' '}
                    <EditableField value={editableDeal.zip} onSave={v => patchEditableDeal('zip', v)} placeholder="ZIP" />
                  </p>
                </div>
              </div>
              <button className="ddm-close" onClick={() => setDetailDeal(null)}>✕</button>
            </div>

            {/* Source pill */}
            <div className="ddm-source-row">
              <span
                className="ddm-source-pill"
                style={{
                  background: editableDeal.source === 'loopnet_live' ? '#dbeafe' : '#eff6ff',
                  color: editableDeal.source === 'loopnet_live' ? '#1d4ed8' : '#1e40af',
                }}
              >
                {editableDeal.source === 'loopnet_live'
                  ? '🟢 Live LoopNet Listing'
                  : editableDeal.source === 'broker_email'
                    ? '📧 Broker Email'
                    : editableDeal.source === 'listings'
                      ? '🏢 Listings Feed'
                      : '📋 Manual Entry'}
              </span>
              {editableDeal.daysOnMarket != null && (
                <span className="ddm-dom">{editableDeal.daysOnMarket} days on market</span>
              )}
              {editableDeal.status && (
                <span className="ddm-status-pill">{editableDeal.status}</span>
              )}
              <span className="ddm-edit-hint">✎ Click any value to edit</span>
            </div>

            {/* Key metrics grid — all fields editable */}
            <div className="ddm-metrics-grid">
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Asking Price</span>
                <strong className="ddm-metric-value">
                  <EditableField
                    value={editableDeal.price}
                    type="number"
                    onSave={v => patchEditableDeal('price', v)}
                    displayFormat={v => v != null ? `$${Number(v).toLocaleString()}` : 'N/A'}
                    placeholder="e.g. 3500000"
                  />
                </strong>
              </div>
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Units</span>
                <strong className="ddm-metric-value">
                  <EditableField
                    value={editableDeal.units}
                    type="number"
                    onSave={v => patchEditableDeal('units', v)}
                    displayFormat={v => v ?? 'N/A'}
                    placeholder="e.g. 48"
                  />
                </strong>
              </div>
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Building Type</span>
                <strong className="ddm-metric-value">
                  <EditableField
                    value={editableDeal.buildingType}
                    onSave={v => patchEditableDeal('buildingType', v)}
                    displayFormat={v => v || 'N/A'}
                    placeholder="Multifamily"
                  />
                </strong>
              </div>
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Year Built</span>
                <strong className="ddm-metric-value">
                  <EditableField
                    value={editableDeal.yearBuilt}
                    type="number"
                    onSave={v => patchEditableDeal('yearBuilt', v)}
                    displayFormat={v => v || 'N/A'}
                    placeholder="e.g. 1985"
                  />
                </strong>
              </div>
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Sq Ft</span>
                <strong className="ddm-metric-value">
                  <EditableField
                    value={editableDeal.squareFeet}
                    type="number"
                    onSave={v => patchEditableDeal('squareFeet', v)}
                    displayFormat={v => v ? Number(v).toLocaleString() : 'N/A'}
                    placeholder="e.g. 42000"
                  />
                </strong>
              </div>
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Cap Rate</span>
                <strong className="ddm-metric-value">
                  <EditableField
                    value={editableDeal.capRate}
                    type="number"
                    onSave={v => patchEditableDeal('capRate', v)}
                    displayFormat={v => v != null ? `${v}%` : 'N/A'}
                    placeholder="e.g. 5.5"
                  />
                </strong>
              </div>
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Price / Unit</span>
                <strong className="ddm-metric-value">
                  {editableDeal.price && editableDeal.units
                    ? `$${Math.round(editableDeal.price / editableDeal.units).toLocaleString()}`
                    : 'N/A'}
                </strong>
              </div>
              <div className="ddm-metric-cell">
                <span className="ddm-metric-label">Price / Sq Ft</span>
                <strong className="ddm-metric-value">
                  {editableDeal.price && editableDeal.squareFeet
                    ? `$${Math.round(editableDeal.price / editableDeal.squareFeet).toLocaleString()}`
                    : 'N/A'}
                </strong>
              </div>
            </div>

            {/* Financial estimates — recalculated from editable values */}
            {(editableDeal.capRate || editableDeal.price) && (
              <div className="ddm-financials">
                <div className="ddm-section-title">📊 Estimated Financials</div>
                <div className="ddm-fin-row">
                  {editableDeal.capRate && editableDeal.price && (
                    <>
                      <div className="ddm-fin-cell">
                        <span>Est. Annual NOI</span>
                        <strong>${Math.round(editableDeal.price * (editableDeal.capRate / 100)).toLocaleString()}</strong>
                        <em>at {editableDeal.capRate}% cap rate</em>
                      </div>
                      <div className="ddm-fin-cell">
                        <span>Est. Monthly Gross Rent</span>
                        <strong>${Math.round((editableDeal.price * (editableDeal.capRate / 100)) / 12 / 0.62).toLocaleString()}</strong>
                        <em>~38% expense ratio assumed</em>
                      </div>
                      <div className="ddm-fin-cell">
                        <span>Est. Loan @ 70% LTV</span>
                        <strong>${Math.round(editableDeal.price * 0.70).toLocaleString()}</strong>
                        <em>Equity: ${Math.round(editableDeal.price * 0.30).toLocaleString()}</em>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* LoopNet live listing fields — also editable */}
            {editableDeal.source === 'loopnet_live' && (
              <div className="ddm-live-section">
                <div className="ddm-section-title">🟢 Live LoopNet Data</div>
                <div className="ddm-live-grid">
                  <div className="ddm-live-cell"><span>Listing Type</span><strong><EditableField value={editableDeal.listingType} onSave={v => patchEditableDeal('listingType', v)} placeholder="—" /></strong></div>
                  <div className="ddm-live-cell"><span>Property Type</span><strong><EditableField value={editableDeal.propertyType} onSave={v => patchEditableDeal('propertyType', v)} placeholder="—" /></strong></div>
                  <div className="ddm-live-cell"><span>County</span><strong><EditableField value={editableDeal.county} onSave={v => patchEditableDeal('county', v)} placeholder="—" /></strong></div>
                  {editableDeal.lotSize && <div className="ddm-live-cell"><span>Lot Size</span><strong><EditableField value={editableDeal.lotSize} type="number" onSave={v => patchEditableDeal('lotSize', v)} displayFormat={v => `${Number(v).toLocaleString()} sqft`} /></strong></div>}
                  {editableDeal.bathrooms && <div className="ddm-live-cell"><span>Bathrooms</span><strong><EditableField value={editableDeal.bathrooms} onSave={v => patchEditableDeal('bathrooms', v)} /></strong></div>}
                  {editableDeal.listedDate && <div className="ddm-live-cell"><span>Listed Date</span><strong>{new Date(editableDeal.listedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong></div>}
                  {editableDeal.sourceUrl && (
                    <div className="ddm-live-cell" style={{ gridColumn: '1 / -1' }}>
                      <span>Listing URL</span>
                      <a href={editableDeal.sourceUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', wordBreak: 'break-all' }}>{editableDeal.sourceUrl}</a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Signals */}
            <div className="ddm-signals">
              <div className="ddm-signal-cell">
                <span>Investor Fit Score</span>
                <strong style={{ color: editableDeal.fitScore >= 75 ? '#1d4ed8' : '#1e3a8a' }}>
                  {editableDeal.fitScore ?? '—'}/100
                </strong>
              </div>
              <div className="ddm-signal-cell">
                <span>AcquireOS Deal Score</span>
                <strong>
                  <EditableField
                    value={editableDeal.score}
                    type="number"
                    onSave={v => patchEditableDeal('score', Math.min(100, Math.max(0, v)))}
                    displayFormat={v => `${v}/100`}
                  />
                </strong>
              </div>
              <div className="ddm-signal-cell">
                <span>State</span>
                <strong>
                  <EditableField value={editableDeal.state} onSave={v => patchEditableDeal('state', v)} />
                </strong>
              </div>
            </div>

            {/* Footer CTA — uses editableDeal so any edits flow through */}
            <div className="ddm-footer">
              <button className="ddm-close-btn" onClick={() => setDetailDeal(null)}>Close</button>
              {dealState.dealName !== editableDeal.name && (
                <button
                  className="ddm-select-btn"
                  onClick={() => {
                    setSelectedDeal(editableDeal); // use edited version
                    setDetailDeal(null);
                  }}
                >
                  Select This Deal
                </button>
              )}
              {dealState.dealName === editableDeal.name && (
                <button
                  className="ddm-continue-btn"
                  onClick={() => { setDetailDeal(null); handleViewDetails(); }}
                >
                  Proceed to Underwriting →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="deal-lead-footer">
        <div className="module-progress">
          <div className="progress-info">
            <span>Step 2 of 15</span>
            <span>Deal Lead Generation</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: '13%' }}></div>
          </div>
        </div>
        <div className="next-steps">
          <h4>Next Steps</h4>
          <ol>
            <li>Review Investor Fit Scores and pick the best-matched deal</li>
            <li>Click "Confirm Deal Selection", then "View Details"</li>
            <li>Proceed to Underwriting Engine</li>
          </ol>
        </div>
      </div>

      <div className="module-bottom-advance">
        <div className={`gate-wrap ${!dealState.dealId ? 'gated' : ''}`}>
          <button
            className={`continue-button ${!dealState.dealId ? 'gated' : ''}`}
            onClick={() => dealState.dealId && handleViewDetails()}
          >
            View Details &amp; Proceed to Underwriting →
          </button>
          {!dealState.dealId && (
            <span className="gate-tooltip">Select and confirm a deal first</span>
          )}
        </div>
      </div>
      {/* ── Part 3: Proceed to Underwriting Popup ── */}
      {showProceedPopup && (
        <div className="proceed-popup-overlay" onClick={() => setShowProceedPopup(false)}>
          <div className="proceed-popup" onClick={e => e.stopPropagation()}>
            <div className="pp-icon"></div>
            <h2 className="pp-title">Deal Confirmed!</h2>
            <div className="pp-deal-name">{dealState.dealName}</div>
            <div className="pp-meta">
              {dealState.rawDealData?.price && (
                <span>${Number(dealState.rawDealData.price).toLocaleString()}</span>
              )}
              {dealState.rawDealData?.property?.units && (
                <span>{dealState.rawDealData.property.units} units</span>
              )}
              {dealState.rawDealData?.location?.city && (
                <span>📍 {dealState.rawDealData.location.city}, {dealState.rawDealData.location.state}</span>
              )}
            </div>
            <p className="pp-message">
              Ready to proceed to Underwriting? The engine will run financial analysis using your deal data.
            </p>
            <button className="pp-cta-btn" onClick={handleGoToUnderwriting}>
              Go to Underwriting Engine →
            </button>
            <button className="pp-cancel-btn" onClick={() => setShowProceedPopup(false)}>
              Back to Deal List
            </button>
          </div>
        </div>
      )}

      {/* ── Part 5: Missing Documents Gate ── */}
      {showMissingDocsGate && (
        <div className="proceed-popup-overlay" onClick={() => setShowMissingDocsGate(false)}>
          <div className="proceed-popup missing-docs-popup" onClick={e => e.stopPropagation()}>
            <div className="pp-icon">⚠️</div>
            <h2 className="pp-title">Missing Documents</h2>
            <div className="pp-deal-name">{dealState.dealName}</div>
            <p className="pp-message missing-docs-msg">
              This deal doesn't include an Offering Memorandum (OM) or required documents needed for underwriting.
              Please choose one of the options below to continue.
            </p>
            <div className="missing-docs-options">
              <button
                className="mdo-btn mdo-btn-secondary"
                onClick={() => {
                  setShowMissingDocsGate(false);
                  resetDeal();
                  setSelectedDeal(null);
                  // Show only deals with OM — filter hint will apply
                }}
              >
                <span className="mdo-icon">🔍</span>
                <div className="mdo-body">
                  <strong>Choose a Different Deal</strong>
                  <span>Go back to deal list filtered to deals with documents</span>
                </div>
              </button>
              <button
                className="mdo-btn mdo-btn-primary"
                onClick={() => {
                  setShowMissingDocsGate(false);
                  forceNavigateToModule(3);
                }}
              >
                <span className="mdo-icon">📁</span>
                <div className="mdo-body">
                  <strong>Upload Documents Manually</strong>
                  <span>Go to Deal Intake and upload your own OM for this deal</span>
                </div>
              </button>
            </div>
            <button className="pp-cancel-btn" onClick={() => setShowMissingDocsGate(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DealLeadPage;
